// ---------- packs: unlockable puzzle collections ----------
// A pack is a named group of puzzles, invisible in the catalogue until unlocked — a rewarded
// video ad on native, free and instant on the web (no AdMob there at all, cf. src/ads.ts:1-6).
// This module owns the full-screen "Packs disponibles" page — same `.on`-toggle pattern as
// src/progression.ts, not the `.dlg` sheet pattern: this is a destination, not a transient
// confirmation — and the chest-reveal celebration that follows a successful unlock.
//
// UNE VITRINE (2026-10-23), et plus une liste de cartes. Trente-trois cartes presque identiques,
// deux et demie par écran, chacune avec son gros bouton « Débloquer » : la page ne donnait envie
// de rien, et rien n'y disait ce qu'un pack contenait. Désormais : une bannière qui tourne (les
// packs épinglés par l'atelier, puis le coup de cœur des joueurs, le plus débloqué, le plus
// récent), une étagère par catégorie qu'on fait défiler de côté, et au toucher la FICHE du pack —
// chaque puzzle avec sa technique, sa durée et sa difficulté, sa note et ses déblocages, et le seul
// bouton de déblocage. Maquette comparée à deux autres pistes :
// https://claude.ai/artifact/JVEUTLSSNYwZe1MWZPRmhx
//
// Toujours ni image ni titre de puzzle avant de l'avoir colorié : les couvertures sont dessinées
// d'après la catégorie et les techniques (src/packCover.ts).

import { currentUser, ensureUser } from './account';
import { fetchAvailablePacks, unlockPack, type PackSummary } from './api';
import { prepareRewardedAd, showRewardedAd } from './ads';
import { noMotion } from './anim';
import { CATEGORY_LABELS, categoryEyebrow, categoryMarkId, isCategorySlug } from './category';
import { confettiBurst, refreshPuzzles } from './home';
import { alphabetLabel, currentLang, difficultyLabel, fmtMinutes, t, type I18nKey } from './i18n';
import { packArguments } from './packArgument';
import { paintCover } from './packCover';
import { isNative } from './platform';
import { techniqueIconLg } from './techniqueIcon';
import { gridDifficulty } from '../shared/difficulty';
import { estimateMinutes } from '../shared/effort';
import { escapeHtml, icon } from './html';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

// ---------- "fraîchement débloqué" ----------
// LA SEULE RAISON du ruban « Nouveau » sur une carte (cf. buildCard dans home.ts). Il en existait
// une deuxième — créé récemment, sur `puzzles.createdAt` — et elle a été retirée : elle allumait
// le ruban chez TOUT LE MONDE en même temps, y compris chez qui ne pouvait pas encore voir la
// feuille, alors que le ruban ne dit qu'une chose, et elle est personnelle : ça vient de s'ouvrir
// POUR TOI. Une date de publication ne sait pas ça.
//
// Ce que ça laisse de côté, et c'est assumé : une feuille ajoutée à un pack déjà débloqué
// n'allumera rien. Un pack publié ne reçoit pas de feuille après coup, sauf exception rare, et
// l'exception ne coûte qu'un ruban manquant.
const FRESH_KEY = 'codigrid:freshlyUnlocked:v1';
// VINGT-QUATRE HEURES, et plus une semaine. Le ruban répond à « qu'est-ce qui a changé depuis la
// dernière fois », pas à « qu'est-ce qui est récent » : au-delà d'un jour, le joueur a rouvert
// l'étagère plusieurs fois et le ruban ne lui apprend plus rien — il devient un ornement, et un
// ornement sur cinq cartes fait perdre au signal ce qui en faisait un.
const FRESH_MS = 24 * 60 * 60 * 1000;

function readFresh(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(FRESH_KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

/** Écrit la carte en ÉLAGUANT au passage ce qui a expiré : sans ça elle grossit d'un pack à
 *  chaque déblocage et ne rétrécit jamais, pour des entrées que plus personne ne lira. */
function writeFresh(map: Record<string, number>): void {
  try {
    const now = Date.now();
    const vivant = Object.fromEntries(Object.entries(map).filter(([, at]) => now - at < FRESH_MS));
    localStorage.setItem(FRESH_KEY, JSON.stringify(vivant));
  } catch {
    /* pas de stockage : le ruban « Nouveau » ne s'affichera simplement pas pour ces puzzles */
  }
}

function markFreshlyUnlocked(ids: string[]): void {
  const map = readFresh();
  const now = Date.now();
  for (const id of ids) map[id] = now;
  writeFresh(map);
}

/** Lu par buildCard() (home.ts), qui n'a plus que ça pour décider du ruban. */
export function isFreshlyUnlocked(id: string): boolean {
  const at = readFresh()[id];
  return typeof at === 'number' && Date.now() - at < FRESH_MS;
}

/**
 * Le ruban s'éteint aussi À L'OUVERTURE, et pas seulement au bout de vingt-quatre heures.
 *
 * Une feuille qu'on a ouverte n'est plus une nouveauté — c'est un travail en cours, et elle a
 * d'ailleurs quitté la bibliothèque pour le flux. L'étagère se nettoie donc au rythme du joueur
 * plutôt qu'à celui d'une horloge : celui qui ouvre les cinq feuilles d'un pack fraîchement
 * débloqué n'a plus un seul ruban en repartant, ce qui est exactement ce qu'il vient de faire.
 *
 * Appelé quand l'ouverture a RÉUSSI, jamais avant : une feuille qu'on n'a pas pu ouvrir n'a pas
 * été vue, et son ruban doit l'attendre.
 */
export function forgetFreshlyUnlocked(id: string): void {
  const map = readFresh();
  if (!(id in map)) return;
  delete map[id];
  writeFresh(map);
}

// ---------- état de l'écran ----------
let packs: PackSummary[] = [];
let loading = true;
/** Le pack à montrer à l'ouverture (cf. openPacksPage) : celui du puzzle le mieux noté de la
 *  semaine, quand « Ensemble » propose de le débloquer. Consommé au premier rendu de la liste :
 *  sa fiche s'ouvre directement. */
let focusId: string | null = null;
/** Le pack dont la fiche est ouverte, ou null. */
let sheetId: string | null = null;
/** La rotation de la bannière, arrêtée à chaque nouveau rendu et à la fermeture de la page. */
let heroTimer = 0;

/** La clé d'étagère d'un pack « Sélection », sans catégorie. */
const NO_CATEGORY = '-';
/** Diapositives de la bannière, au plus : au-delà, on ne les voit plus passer. */
const HERO_MAX = 4;
/** Le temps d'une diapositive. Assez pour lire le nom et la raison, pas assez pour s'ennuyer. */
const HERO_MS = 4500;
/** Le « coup de cœur des joueurs » automatique demande ce nombre de notes : sans plancher, un
 *  pack noté 5 par un seul joueur passerait devant un consensus à 4,8 sur trente. */
const LOVED_VOTES = 10;
/** En dessous, la note n'est pas affichée du tout : « ★ 5 · 1 note » ne dit rien. */
const SHOWN_VOTES = 3;
/** Le ruban « Nouveau » d'une vignette : un pack publié depuis moins d'une semaine. Deux
 *  semaines l'allumaient sur les huit packs de monuments sortis le même jour — un ruban sur
 *  huit vignettes ne distingue plus rien (vu le 2026-10-23). */
const NEW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Ce que débloquer coûte réellement, dit DEUX FOIS sur la fiche : une fois dans le bouton (le
 * geste : regarder une pub) et une fois sous lui (le prix : gratuit, une trentaine de secondes).
 *
 * La répétition n'est pas une maladresse, c'est le sujet : le retour des joueurs est qu'on croit
 * un bouton « Débloquer » payant, et un seul rappel discret ne suffit pas à lever ce doute.
 *
 * Web : pas de pub du tout, gratuit et instantané (cf. handleUnlock()) — le bouton dit alors
 * simplement « Débloquer », et la ligne « Gratuit, en un tap ». Ne jamais promettre une pub qui
 * ne jouera pas.
 */
function unlockLabel(): string {
  return isNative() ? t('packs.watchAd') : t('packs.unlock');
}

/** Le contenu du bouton — icône comprise. Sert au premier rendu ET au retour à l'état initial
 *  après un échec : `textContent` seul effaçait le pictogramme et remplaçait « Regarder une pub »
 *  par « Débloquer ». */
function ctaContent(): string {
  return `${isNative() ? icon('i-play') : ''}${unlockLabel()}`;
}

function freeLine(): string {
  return isNative() ? t('packs.adNote') : t('packs.freeNote');
}

// ---------- ce qu'on dit d'un pack ----------

/**
 * Le nom qu'on montre : le THÈME, traduit par le serveur, numéroté quand plusieurs packs le
 * partagent ; à défaut le titre de l'atelier, comme avant les thèmes. Le titre est le nom de
 * travail, en français : le joueur allemand lisait « Animals (vol 1) » (cf.
 * backend/src/utils/packThemes.ts).
 */
function packName(p: PackSummary): string {
  const base = p.themeTitle || p.title;
  return p.volume ? t('packs.volume', { title: base, n: p.volume }) : base;
}

/** Les minutes d'un puzzle, sur l'échelle grossière des cartes de la Bibliothèque. */
function sheetMinutes(x: { zones: number | null }): number {
  return estimateMinutes(x.zones ?? 0);
}

/** La durée d'un pack entier, arrondie au quart d'heure : la somme de ses puzzles. Pas `fmtMinutes`,
 *  qui plafonne à « 2 h+ » — juste pour une feuille, trop vague pour un pack de cinq. */
function packMinutes(p: PackSummary): number {
  return (p.puzzles ?? []).reduce((s, x) => s + sheetMinutes(x), 0);
}
function fmtPackTime(m: number): string {
  if (m <= 0) return '';
  if (m < 60) return t('time.min', { n: m });
  const q = Math.round(m / 15) * 15;
  const h = Math.floor(q / 60);
  const rest = q % 60;
  return rest ? t('time.hmin', { h, m: rest }) : t('time.h', { n: h });
}

/** La note, avec la virgule ou le point de la langue. Null sous SHOWN_VOTES notes. */
function ratingText(p: PackSummary): string | null {
  if (p.rating == null || (p.votes ?? 0) < SHOWN_VOTES) return null;
  const n = new Intl.NumberFormat(currentLang(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(p.rating);
  return `★ ${n}`;
}

/** Quand le pack est sorti : sa première publication, et à défaut sa création — un serveur d'avant
 *  la colonne, ou un pack publié avant elle. Sur createdAt seul, « Nouveau » datait le BROUILLON :
 *  un pack de Noël préparé un mois avant sortait sans ruban, et la bannière disait « Nouveau »
 *  d'un pack de mai (revue du 2026-10-23). */
function publishedOf(p: PackSummary): string {
  return p.publishedAt ?? p.createdAt ?? '';
}

/** Le plus récemment sorti d'abord. */
const byRelease = (a: PackSummary, b: PackSummary): number =>
  publishedOf(b).localeCompare(publishedOf(a));

/** « puzzle » ou « puzzles » après un nombre : un pack d'une seule feuille affichait « 1 puzzles »
 *  sur sa tuile, dans la bannière et sur sa fiche. */
function puzzlesWord(n: number): string {
  return t(n === 1 ? 'packs.puzzlesLabel.one' : 'packs.puzzlesLabel.other');
}

function isNew(p: PackSummary): boolean {
  const s = publishedOf(p);
  const at = s ? Date.parse(s.replace(' ', 'T') + 'Z') : NaN;
  return Number.isFinite(at) && Date.now() - at < NEW_MS;
}

/** La clé d'étagère d'un pack : sa catégorie, ou NO_CATEGORY pour un pack « Sélection ». */
function catOf(p: PackSummary): string {
  return isCategorySlug(p.category) ? p.category : NO_CATEGORY;
}

function catLabel(key: string): string {
  return key === NO_CATEGORY ? t('packs.selection') : categoryEyebrow(key);
}

/** L'échappement partagé (cf. html.ts) : la copie locale qui vivait ici n'échappait pas `'`. */
const esc = escapeHtml;

// ---------- la bannière ----------

interface HeroSlide {
  pack: PackSummary;
  /** L'étiquette : celle de l'atelier pour un pack épinglé, sinon la raison du choix. */
  why: string;
}

/**
 * Les diapositives de la bannière : d'abord les packs que l'atelier a épinglés (dans l'ordre
 * qu'il a choisi, les plus récents ensuite), puis, pour qu'elle ne soit jamais vide ni figée, trois
 * choix faits par des RÈGLES — le coup de cœur des joueurs (la meilleure note sur LOVED_VOTES
 * notes au moins), le plus débloqué, le plus récent. Un pack n'y paraît qu'une fois, et seulement
 * s'il reste à débloquer : la liste ne contient que ceux-là.
 */
function heroSlides(list: readonly PackSummary[]): HeroSlide[] {
  const out: HeroSlide[] = [];
  const has = (p: PackSummary) => out.some((s) => s.pack.id === p.id);
  const recent = byRelease;
  const pinned = list
    .filter((p) => p.featured)
    .sort(
      (a, b) => (a.featured!.order ?? Infinity) - (b.featured!.order ?? Infinity) || recent(a, b),
    );
  for (const p of pinned) {
    // Une étiquette ajoutée au Worker après cette version : t() rend '' pour une clé qu'il ne
    // connaît pas, et le pack reste en tête, sans raison écrite.
    const why = t(`packs.feature.${p.featured!.tag}` as I18nKey);
    out.push({ pack: p, why });
  }
  const auto: [PackSummary | undefined, I18nKey][] = [
    [
      list
        .filter((p) => (p.votes ?? 0) >= LOVED_VOTES && p.rating != null)
        .sort((a, b) => b.rating! - a.rating! || (b.votes ?? 0) - (a.votes ?? 0))[0],
      'packs.feature.joueurs',
    ],
    [
      list.filter((p) => (p.unlocks ?? 0) > 0).sort((a, b) => b.unlocks! - a.unlocks!)[0],
      'packs.feature.populaire',
    ],
    // « Nouveau » seulement s'il l'est (cf. isNew) : le plus récent d'une vitrine restée
    // inchangée un mois n'a plus rien de nouveau, et sa tuile n'a d'ailleurs pas le ruban.
    [[...list].filter(isNew).sort(recent)[0], 'packs.feature.nouveau'],
  ];
  for (const [p, key] of auto) if (p && !has(p)) out.push({ pack: p, why: t(key) });
  return out.slice(0, HERO_MAX);
}

function heroLine(p: PackSummary): string {
  return [
    `${p.puzzleCount} ${puzzlesWord(p.puzzleCount)}`,
    fmtPackTime(packMinutes(p)),
    ratingText(p),
  ]
    .filter(Boolean)
    .join(' · ');
}

function hero(slides: HeroSlide[]): string {
  if (!slides.length) return '';
  return `
    <div class="pk-hero" role="region" aria-roledescription="${esc(t('packs.carousel'))}" aria-label="${esc(t('packs.title'))}">
      ${slides
        .map(
          (s, i) => `
        <button class="pk-slide${i === 0 ? ' on' : ''}" data-open="${s.pack.id}" ${i ? 'tabindex="-1" aria-hidden="true"' : ''}>
          <canvas data-cover="${s.pack.id}" data-cells="10"></canvas>
          <span class="pk-veil"></span>
          <span class="pk-slide-txt">
            ${s.why ? `<span class="pk-why">${esc(s.why)}</span>` : ''}
            <span class="pk-slide-name">${esc(packName(s.pack))}</span>
            <span class="pk-slide-meta">${heroLine(s.pack)}</span>
          </span>
        </button>`,
        )
        .join('')}
      ${slides.length > 1 ? `<span class="pk-dots">${slides.map((_, i) => `<i class="${i === 0 ? 'on' : ''}"></i>`).join('')}</span>` : ''}
    </div>`;
}

/**
 * La rotation, et le glissé au doigt. Un glissé de plus de 40 px change de diapositive ; en deçà,
 * c'est un tap, qui ouvre la fiche (le clic remonte jusqu'au bouton). Sous « réduire les
 * animations », pas de rotation : on change de diapositive au doigt seulement.
 */
function wireHero(body: HTMLElement): void {
  window.clearInterval(heroTimer);
  const h = body.querySelector<HTMLElement>('.pk-hero');
  if (!h) return;
  const slides = [...h.querySelectorAll<HTMLElement>('.pk-slide')];
  const dots = [...h.querySelectorAll<HTMLElement>('.pk-dots i')];
  if (slides.length < 2) return;
  let at = 0;
  const show = (i: number) => {
    at = (i + slides.length) % slides.length;
    slides.forEach((s, k) => {
      s.classList.toggle('on', k === at);
      s.tabIndex = k === at ? 0 : -1;
      s.setAttribute('aria-hidden', String(k !== at));
    });
    dots.forEach((d, k) => d.classList.toggle('on', k === at));
  };
  const rotate = () => {
    window.clearInterval(heroTimer);
    if (!noMotion()) heroTimer = window.setInterval(() => show(at + 1), HERO_MS);
  };
  let x0: number | null = null;
  let swiped = false;
  h.addEventListener('pointerdown', (e) => {
    x0 = e.clientX;
    swiped = false;
  });
  h.addEventListener('pointerup', (e) => {
    if (x0 === null) return;
    const dx = e.clientX - x0;
    x0 = null;
    if (Math.abs(dx) < 40) return;
    swiped = true;
    show(at + (dx < 0 ? 1 : -1));
    rotate();
  });
  // Le clic qui suit un glissé n'ouvre rien : le doigt voulait changer de diapositive.
  h.addEventListener(
    'click',
    (e) => {
      if (!swiped) return;
      e.stopPropagation();
      swiped = false;
    },
    true,
  );
  rotate();
}

// ---------- les étagères ----------

function tile(p: PackSummary): string {
  const meta = [fmtPackTime(packMinutes(p)), ratingText(p)].filter(Boolean).join(' · ');
  return `
    <button class="pk-tile" data-open="${p.id}">
      <span class="pk-cover">
        <canvas data-cover="${p.id}"></canvas>
        <span class="pk-count">${p.puzzleCount} ${puzzlesWord(p.puzzleCount)}</span>
        ${isNew(p) ? `<span class="pk-new">${t('packs.feature.nouveau')}</span>` : ''}
        <svg class="pk-glyph" aria-hidden="true"><use href="#${categoryMarkId(p.category)}"></use></svg>
      </span>
      <span class="pk-name">${esc(packName(p))}</span>
      ${meta ? `<span class="pk-meta">${meta}</span>` : ''}
    </button>`;
}

/** Une étagère par catégorie présente, dans l'ordre du filtre du catalogue, « Sélection » en
 *  dernier ; les packs de chacune, du plus récent au plus ancien. */
function shelves(): string {
  const order = [...Object.keys(CATEGORY_LABELS), NO_CATEGORY];
  const keys = [...new Set(packs.map(catOf))].sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib);
  });
  return keys
    .map((k) => {
      const list = packs.filter((p) => catOf(p) === k).sort(byRelease);
      return `
      <section class="pk-shelf">
        <div class="pk-shelf-head">
          <h2>${icon(categoryMarkId(k === NO_CATEGORY ? null : k))}${esc(catLabel(k))}</h2>
          ${list.length > 1 ? `<span>${t('packs.shelf.n', { n: list.length })}</span>` : ''}
        </div>
        <div class="pk-row">${list.map(tile).join('')}</div>
      </section>`;
    })
    .join('');
}

/** Peint les couvertures d'un morceau de page, une fois qu'il est posé (il faut sa taille). */
/**
 * LES COUVERTURES, PEINTES EN ARRIVANT À L'ÉCRAN (revue du 2026-10-23). Toutes d'un coup, c'était
 * une trentaine de vignettes dont les quatre cinquièmes hors de l'écran, plus les diapositives
 * cachées, jusqu'à 2,5 pixels par point : environ 26 Mo de canevas sur un Pixel 7a, le double du
 * budget que render.ts s'impose pour la feuille (SHEET_BUDGET) parce qu'au-delà les WebView d'entrée
 * de gamme rechargent la page ou rendent un canevas blanc. Un observateur les peint quand elles
 * approchent ; releaseCovers() les rend à la fermeture de la page.
 */
let coverObserver: IntersectionObserver | null = null;

function paintCovers(root: HTMLElement): void {
  coverObserver ??= new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const cv = e.target as HTMLCanvasElement;
        coverObserver?.unobserve(cv);
        const p = packs.find((q) => q.id === cv.dataset['cover']);
        if (p) paintCover(cv, p, Number(cv.dataset['cells']) || undefined);
      }
    },
    { rootMargin: '160px' },
  );
  root
    .querySelectorAll<HTMLCanvasElement>('canvas[data-cover]')
    .forEach((cv) => coverObserver!.observe(cv));
}

/** La page se ferme : ses canevas partent avec son contenu, et l'observateur avec eux. */
function releaseCovers(): void {
  coverObserver?.disconnect();
  coverObserver = null;
  $('packsBody').replaceChildren();
  $('packSheet').replaceChildren();
}

function renderList(): void {
  const body = $('packsBody');
  window.clearInterval(heroTimer);
  if (loading) {
    body.innerHTML = `<p class="packs-empty">${t('packs.loading')}</p>`;
    return;
  }
  if (!packs.length) {
    body.innerHTML = `<p class="packs-empty">${t('packs.empty')}</p>`;
    return;
  }
  const total = packs.reduce((s, p) => s + p.puzzleCount, 0);
  body.innerHTML =
    (total > 1 ? `<p class="pk-intro">${t('packs.intro', { n: total })}</p>` : '') +
    hero(heroSlides(packs)) +
    shelves();
  body.querySelectorAll<HTMLElement>('[data-open]').forEach((el) => {
    el.onclick = () => openSheet(el.dataset['open']!);
  });
  wireHero(body);
  paintCovers(body);
  // Le pack demandé (depuis « Ensemble ») : sa fiche s'ouvre d'elle-même. S'il n'est pas dans la
  // liste — déjà débloqué, ou d'une catégorie que cette version ne sait pas nommer —, la page
  // s'ouvre simplement, sans rien désigner.
  if (focusId) {
    const id = focusId;
    focusId = null;
    if (packs.some((p) => p.id === id)) openSheet(id);
  }
}

// ---------- la fiche d'un pack ----------

/** Une ligne par puzzle : sa technique, sa durée, sa difficulté — ni image ni titre, c'est la
 *  règle du catalogue (cf. packCover.ts). Triés du plus court au plus long.
 *
 *  La technique en TITRE de ligne, avec ses quatre cases (la même icône 2×2 que les cartes de la
 *  Bibliothèque) : c'est la seule chose qui distingue deux puzzles avant de les ouvrir. La
 *  première version posait une feuille « ? » en pointillés avec la marque en coin, et
 *  « Puzzle 1 · » en tête du titre : trois cartes encadrées, un point d'interrogation qui
 *  répétait la légende juste au-dessus, une marque de 24 px qui débordait de sa feuille —
 *  « moche » (2026-10-23). Le numéro passe en seconde ligne, à côté de la durée. */
function insideRows(p: PackSummary): string {
  const list = [...(p.puzzles ?? [])].sort((a, b) => sheetMinutes(a) - sheetMinutes(b));
  return list
    .map((x, i) => {
      const d = gridDifficulty(x.gridW, x.gridH).key;
      const m = sheetMinutes(x);
      return `
      <div class="ps-row">
        <span class="ps-tech">${techniqueIconLg(x.alphabetId)}</span>
        <span class="ps-what"><b>${esc(alphabetLabel(x.alphabetId))}</b><small>${t('packs.sheet.puzzle', { n: i + 1 })}${m ? ` · ≈ ${fmtMinutes(m)}` : ''}</small></span>
        <span class="ps-lv diff-${d}">${difficultyLabel(d)}</span>
      </div>`;
    })
    .join('');
}

function openSheet(id: string): void {
  const p = packs.find((q) => q.id === id);
  if (!p) return;
  sheetId = id;
  const time = fmtPackTime(packMinutes(p));
  const rating = ratingText(p);
  const args = packArguments(p).map((a) => `<span class="pack-arg">${a}</span>`);
  if ((p.unlocks ?? 0) >= 2)
    args.push(`<span class="pack-arg">${t('packs.unlocks', { n: p.unlocks! })}</span>`);
  const sheet = $('packSheet');
  sheet.innerHTML = `
    <div class="ps-scroll">
      <div class="ps-cover"><canvas data-cover="${p.id}" data-cells="9"></canvas>
        <button class="ps-close" id="packSheetClose" aria-label="${esc(t('common.close'))}">${icon('i-close')}</button>
      </div>
      <p class="ps-eyebrow">${esc(catLabel(catOf(p)))}</p>
      <h2 class="ps-title" id="packSheetTitle">${esc(packName(p))}</h2>
      <div class="ps-facts">
        <div><b>${p.puzzleCount}</b><span>${puzzlesWord(p.puzzleCount)}</span></div>
        ${time ? `<div><b>${time}</b><span>${t('packs.fact.time')}</span></div>` : ''}
        <div><b>${rating ?? '—'}</b><span>${rating ? t('packs.fact.votes', { n: p.votes! }) : t('packs.fact.unrated')}</span></div>
      </div>
      ${args.length ? `<div class="pack-args">${args.join('')}</div>` : ''}
      ${
        p.puzzles?.length
          ? `<p class="ps-inside">${t('packs.inside')}<small>${t('packs.inside.hint')}</small></p>
             <div class="ps-list">${insideRows(p)}</div>`
          : ''
      }
    </div>
    <div class="ps-foot">
      <button class="pack-card-cta" id="packSheetCta">${ctaContent()}</button>
      <span class="pack-card-note">${icon('i-clock')}<span>${freeLine()}</span></span>
    </div>`;
  $('packs').classList.add('sheet-on');
  sheetShown(true);
  $('packSheetClose').onclick = () => closeSheet();
  const cta = $<HTMLButtonElement>('packSheetCta');
  cta.onclick = () => void handleUnlock(id, cta);
  // Un déblocage en cours (la pub qui charge, l'appel au serveur) survit à la fiche qui l'a lancé :
  // rouverte, elle montre un bouton fermé plutôt qu'un bouton neuf qui en lancerait un second.
  if (unlocking) {
    cta.disabled = true;
    if (unlocking === id) cta.textContent = t('packs.unlocking');
  }
  paintCovers(sheet);
}

/** La fiche glisse hors de l'écran sans disparaître du document : fermée, elle doit aussi sortir
 *  du parcours du clavier et des lecteurs d'écran — son « Débloquer » restait atteignable, et
 *  s'activait sans rien à l'écran (revue du 2026-10-23). Même convention que les .dlg. */
function sheetShown(on: boolean): void {
  const sheet = $('packSheet');
  sheet.inert = !on;
  if (on) sheet.removeAttribute('aria-hidden');
  else sheet.setAttribute('aria-hidden', 'true');
}

function closeSheet(): boolean {
  if (!sheetId) return false;
  sheetId = null;
  $('packs').classList.remove('sheet-on');
  sheetShown(false);
  return true;
}

/** Le numéro du dernier chargement lancé : une réponse plus ancienne n'a plus rien à afficher. */
let refreshSeq = 0;

async function refresh(): Promise<void> {
  const seq = ++refreshSeq;
  loading = true;
  renderList();
  let list: PackSummary[] = [];
  try {
    list = await fetchAvailablePacks(await currentUser());
  } catch {
    list = [];
  }
  // La page a été fermée (ou rouverte) pendant le chargement : rien à peindre. Peinte quand même,
  // la fiche d'un pack demandé s'ouvrait sur la page cachée, le « précédent » suivant d'Android
  // la fermait sans rien montrer, et la bannière tournait dans le vide (revue du 2026-10-23).
  if (seq !== refreshSeq || !$('packs').classList.contains('on')) return;
  packs = list;
  loading = false;
  renderList();
}

/** Le pack en cours de déblocage. Le `disabled` du bouton ne suffisait pas : la fiche se reconstruit
 *  à chaque ouverture, et la refermer puis la rouvrir pendant la pub donnait un bouton neuf — une
 *  seconde pub, un second appel, un second coffre (revue du 2026-10-23). Un seul à la fois. */
let unlocking: string | null = null;

async function handleUnlock(packId: string, btn: HTMLButtonElement): Promise<void> {
  // Un deuxième tap pendant la pub ou l'appel au serveur ne relance rien.
  if (btn.disabled || unlocking) return;
  btn.disabled = true;
  unlocking = packId;
  try {
    if (isNative()) {
      // « Préparation… » et pas « Déblocage… » : showRewardedAd() attend maintenant la fin du
      // chargement quand le joueur a tapé trop tôt, et cette attente-là doit se voir, sinon elle
      // se lit comme un tap perdu.
      btn.textContent = t('packs.adLoading');
      const outcome = await showRewardedAd();
      if (outcome !== 'rewarded') {
        // Deux échecs, deux phrases : « aucune pub à te montrer » n'est pas « tu l'as coupée ».
        // Les confondre — ce que faisait le retour silencieux au libellé d'origine — laissait le
        // joueur retaper indéfiniment sans jamais savoir ce qu'on attendait de lui.
        return fail(btn, outcome === 'skipped' ? 'packs.adSkipped' : 'packs.adUnavailable');
      }
    }
    btn.textContent = t('packs.unlocking');
    const token = await ensureUser();
    if (!token) throw new Error('offline');
    const result = await unlockPack(token, packId);
    markFreshlyUnlocked(result.puzzleIds);
    // Le nom du thème, dans la langue du joueur, et pas le titre de l'atelier que rend la route.
    const shown = packs.find((p) => p.id === packId);
    packs = packs.filter((p) => p.id !== packId);
    closeSheet();
    // La page a pu être fermée pendant la pub : le coffre s'ouvre quand même (la récompense est
    // acquise), mais rien n'est à repeindre derrière.
    if ($('packs').classList.contains('on')) renderList();
    showReveal(shown ? packName(shown) : result.title, result.puzzleIds.length);
  } catch {
    fail(btn, 'packs.adFailed');
  } finally {
    unlocking = null;
  }
}

/** Rend la main au joueur en lui disant ce qui a manqué, puis remet le bouton dans son état
 *  d'origine — libellé natif et icône compris. */
function fail(btn: HTMLButtonElement, key: I18nKey): void {
  btn.disabled = false;
  btn.textContent = t(key);
  window.setTimeout(() => {
    btn.innerHTML = ctaContent();
  }, 2400);
}

function showReveal(title: string, count: number): void {
  $('packsRevealTitle').textContent = t(
    count === 1 ? 'packs.reveal.title.one' : 'packs.reveal.title.other',
    { n: count },
  );
  $('packsRevealSub').textContent = t('packs.reveal.sub', { title });
  $('packsReveal').classList.add('on');
  const rect = $('packsRevealChest').getBoundingClientRect();
  confettiBurst(rect.left + rect.width / 2, rect.top + rect.height / 2);
}

function closeReveal(): void {
  $('packsReveal').classList.remove('on');
  // Un RAFRAÎCHISSEMENT et non un chargement : les puzzles du pack doivent apparaître, mais le
  // joueur ne doit pas bouger d'onglet — il est sur la Bibliothèque, c'est là que sa récompense
  // s'affiche, et le catalogue est en ce moment caché derrière cette page. Voir refreshPuzzles()
  // dans src/home.ts, qui porte le détail de ce que loadPuzzles() faisait de travers ici.
  void refreshPuzzles();
}

/** `focus` : l'identifiant d'un pack à amener à l'écran, cf. renderList. */
export function openPacksPage(focus?: string): void {
  focusId = focus ?? null;
  closeSheet();
  $('packs').classList.add('on');
  void refresh();
  if (isNative()) void prepareRewardedAd();
}

/** Même contrat que closeProgression()/closeFacetSheet() — ce que la chaîne du bouton retour
 *  Android (src/main.ts) a besoin de savoir pour décider si le geste est consommé. Le coffre,
 *  posé par-dessus la page Packs, se ferme en premier. */
export function closePacksPage(): boolean {
  if ($('packsReveal').classList.contains('on')) {
    closeReveal();
    return true;
  }
  if (closeSheet()) return true;
  if (!$('packs').classList.contains('on')) return false;
  $('packs').classList.remove('on');
  window.clearInterval(heroTimer);
  releaseCovers();
  return true;
}

/** L'ouverture elle-même est câblée par wireHome() (src/home.ts) sur #packsBanner — le bandeau
 *  EST le déclencheur, pas un bouton à part. */
export function wirePacks(): void {
  sheetShown(false);
  $('packsBack').onclick = () => closePacksPage();
  $('packSheetScrim').onclick = () => closeSheet();
  $('packsRevealContinue').onclick = () => closeReveal();
}

/** Combien de packs restent à débloquer — pour la pastille du bandeau de Bibliothèque, peinte
 *  par home.ts sans jamais bloquer le chargement du catalogue (cf. le plan). */
export async function availablePackCount(): Promise<number> {
  try {
    const list = await fetchAvailablePacks(await currentUser());
    teaser = teaserSlides(list);
    teaserPuzzles = list.reduce((n, p) => n + p.puzzleCount, 0);
    return list.length;
  } catch {
    teaser = [];
    teaserPuzzles = 0;
    return 0;
  }
}

// ---------- l'affiche de la Bibliothèque ----------
// Le bandeau qui mène ici depuis la Bibliothèque (#packsBanner, cf. paintBanner dans home.ts).
// C'était une icône de téléchargement et « Télécharger de nouveaux puzzles » : une notice, qui ne
// montrait rien de ce qu'elle promettait (« très moche », 2026-10-23). C'est maintenant une
// petite affiche : les couvertures des packs que la vitrine met en tête, en éventail, et combien
// de puzzles attendent. AUCUN NOM de pack : la première version portait celui de la tête de la
// vitrine avec son étiquette (« De saison · Icônes du XXe »), mais ce bouton mène à la page des
// packs, pas à l'un d'eux — mettre un pack en avant est le travail de la bannière, là-bas.

/** Les packs de l'affiche, gardés par availablePackCount() qui vient de les recevoir : pas de
 *  seconde requête pour les dessiner. */
let teaser: HeroSlide[] = [];
/** Les puzzles de tous les packs à débloquer, pour « 12 puzzles à découvrir ». */
let teaserPuzzles = 0;

/** Les trois premières diapositives de la vitrine, complétées par les plus récents quand elle en
 *  a moins : l'affiche annonce ce que la page montrera en arrivant. */
function teaserSlides(list: readonly PackSummary[]): HeroSlide[] {
  const out = heroSlides(list).slice(0, 3);
  const recent = [...list].sort(byRelease);
  for (const p of recent) {
    if (out.length >= 3) break;
    if (!out.some((s) => s.pack.id === p.id)) out.push({ pack: p, why: '' });
  }
  return out;
}

/** Combien de puzzles contiennent, ensemble, les packs à débloquer. */
export function teaserPuzzleCount(): number {
  return teaserPuzzles;
}

/** Les couvertures de l'affiche, du dernier au premier pour que le premier soit dessus. À
 *  rappeler quand le bandeau devient visible : un canevas caché n'a pas de taille à peindre. */
export function paintTeaserFan(fan: HTMLElement): void {
  fan.innerHTML = teaser
    .map((s, i) => `<canvas class="hpb-card c${i}" data-teaser="${esc(s.pack.id)}"></canvas>`)
    .reverse()
    .join('');
  requestAnimationFrame(() => {
    fan.querySelectorAll<HTMLCanvasElement>('canvas[data-teaser]').forEach((cv) => {
      const s = teaser.find((x) => x.pack.id === cv.dataset['teaser']);
      if (s) paintCover(cv, s.pack, 5);
    });
  });
}
